import React, { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { Slider } from '../../../ui/slider';
import { Button } from '../../../ui/button';
import { Trash2, ChevronLeft, ChevronRight, Gauge, SunMedium, SunDim, Activity, Pencil } from 'lucide-react';
import { ANIMATIONS, TRANSITIONS, SPEED_PRESETS, SPEED_RAMPS, getEffDur } from './constants';
import { SpeedRampCurve } from './SpeedRampCurve';
import { SpeedRampEditor } from './SpeedRampEditor';

export const ClipEditor = ({ track, trackIdx, tracksCount, onUpdate, onRemove, onMove }) => {
  const effDur = getEffDur(track);
  const activeRamp = track.speed_ramp || 'none';
  const [showCustomEditor, setShowCustomEditor] = useState(activeRamp === 'custom');

  const handleCustomRampChange = (newPoints) => {
    onUpdate({ speed_ramp: 'custom', speed_factor: 1, speed_ramp_points: newPoints });
  };

  const handleSelectCustom = () => {
    const defaultPts = track.speed_ramp_points && track.speed_ramp_points.length >= 2
      ? track.speed_ramp_points
      : [[0, 1], [0.5, 2], [1, 1]];
    onUpdate({ speed_ramp: 'custom', speed_factor: 1, speed_ramp_points: defaultPts });
    setShowCustomEditor(true);
  };

  return (
    <div className="px-3 py-2 border-t border-border/30 space-y-2" data-testid="timeline-detail-editor">
      {/* Row 1: Duration, Animation, Transition, Move, Delete */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          <label className="text-[10px] text-muted-foreground">Dur</label>
          <Slider
            value={[track.duration || 3]}
            onValueChange={([v]) => onUpdate({ duration: v })}
            min={0.5} max={track.type === 'video' ? 60 : 10} step={0.5} className="w-20"
          />
          <span className="text-[10px] font-mono w-6">{track.duration}s</span>
        </div>
        {track.type === 'photo' && (
          <div className="flex items-center gap-1">
            <label className="text-[10px] text-muted-foreground">Anim</label>
            <Select value={track.animation || 'none'} onValueChange={(v) => onUpdate({ animation: v })}>
              <SelectTrigger className="w-24 h-6 text-[10px]"><SelectValue /></SelectTrigger>
              <SelectContent>{ANIMATIONS.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <div className="flex items-center gap-1">
          <label className="text-[10px] text-muted-foreground">Trans</label>
          <Select value={track.transition || 'fade'} onValueChange={(v) => onUpdate({ transition: v })}>
            <SelectTrigger className="w-20 h-6 text-[10px]"><SelectValue /></SelectTrigger>
            <SelectContent>{TRANSITIONS.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-0.5 ml-auto">
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onMove('left')} disabled={trackIdx === 0} data-testid="move-clip-left">
            <ChevronLeft className="h-3 w-3" />
          </Button>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onMove('right')} disabled={trackIdx === tracksCount - 1} data-testid="move-clip-right">
            <ChevronRight className="h-3 w-3" />
          </Button>
          <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={onRemove} data-testid="remove-clip-btn">
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {/* Row 2: Constant Speed presets + Fades */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1" data-testid="speed-control">
          <Gauge className="h-3 w-3 text-amber-500" />
          <div className="flex gap-0.5 flex-wrap">
            {SPEED_PRESETS.map(sp => (
              <button
                key={sp}
                onClick={() => { onUpdate({ speed_factor: sp, speed_ramp: 'none' }); setShowCustomEditor(false); }}
                className={`px-1 py-0.5 text-[8px] rounded font-mono transition-colors ${
                  (track.speed_factor || 1) === sp && activeRamp === 'none'
                    ? 'bg-amber-500/20 text-amber-400 ring-1 ring-amber-500/40'
                    : 'text-muted-foreground hover:bg-muted/40'
                }`}
                data-testid={`speed-preset-${sp}`}
              >
                {sp}x
              </button>
            ))}
          </div>
          <span className="text-[9px] text-muted-foreground">={effDur.toFixed(1)}s</span>
        </div>
        <div className="flex items-center gap-1" data-testid="fade-in-control">
          <SunMedium className="h-2.5 w-2.5 text-blue-400" />
          <Slider value={[track.fade_in || 0]} onValueChange={([v]) => onUpdate({ fade_in: v })} min={0} max={Math.min(3, effDur / 2)} step={0.1} className="w-14" />
          <span className="text-[9px] font-mono">{(track.fade_in || 0).toFixed(1)}</span>
        </div>
        <div className="flex items-center gap-1" data-testid="fade-out-control">
          <SunDim className="h-2.5 w-2.5 text-orange-400" />
          <Slider value={[track.fade_out || 0]} onValueChange={([v]) => onUpdate({ fade_out: v })} min={0} max={Math.min(3, effDur / 2)} step={0.1} className="w-14" />
          <span className="text-[9px] font-mono">{(track.fade_out || 0).toFixed(1)}</span>
        </div>
      </div>

      {/* Row 3: Speed Ramp Picker with visual curves + Custom button */}
      <div className="flex items-center gap-2 pt-0.5" data-testid="speed-ramp-picker">
        <Activity className="h-3 w-3 text-purple-400 flex-shrink-0" />
        <span className="text-[10px] text-muted-foreground flex-shrink-0">Ramp:</span>
        <div className="flex gap-1 flex-wrap">
          {Object.entries(SPEED_RAMPS).map(([id, ramp]) => (
            <button
              key={id}
              onClick={() => { onUpdate({ speed_ramp: id, ...(id === 'none' ? {} : { speed_factor: 1 }) }); setShowCustomEditor(false); }}
              className={`flex items-center gap-1 px-1.5 py-0.5 rounded-md border transition-all ${
                activeRamp === id
                  ? 'border-purple-500/60 bg-purple-500/10 shadow-sm shadow-purple-500/10'
                  : 'border-border/30 hover:border-border/60 hover:bg-muted/30'
              }`}
              data-testid={`speed-ramp-${id}`}
              title={ramp.name}
            >
              <SpeedRampCurve rampId={id} width={36} height={16} active={activeRamp === id} />
              <span className={`text-[7px] font-medium whitespace-nowrap ${activeRamp === id ? 'text-purple-400' : 'text-muted-foreground'}`}>
                {ramp.name}
              </span>
            </button>
          ))}
          {/* Custom ramp button */}
          <button
            onClick={handleSelectCustom}
            className={`flex items-center gap-1 px-1.5 py-0.5 rounded-md border transition-all ${
              activeRamp === 'custom'
                ? 'border-purple-500/60 bg-purple-500/10 shadow-sm shadow-purple-500/10'
                : 'border-border/30 hover:border-border/60 hover:bg-muted/30'
            }`}
            data-testid="speed-ramp-custom"
            title="Custom speed curve"
          >
            {activeRamp === 'custom' && track.speed_ramp_points ? (
              <SpeedRampCurve rampId="custom" width={36} height={16} active customPoints={track.speed_ramp_points} />
            ) : (
              <Pencil className="h-3 w-3 text-purple-400" />
            )}
            <span className={`text-[7px] font-medium whitespace-nowrap ${activeRamp === 'custom' ? 'text-purple-400' : 'text-muted-foreground'}`}>
              Custom
            </span>
          </button>
        </div>
      </div>

      {/* Row 4: Custom Ramp Editor (expanded inline) */}
      {activeRamp === 'custom' && showCustomEditor && (
        <SpeedRampEditor
          points={track.speed_ramp_points}
          onChange={handleCustomRampChange}
          duration={track.duration || 3}
        />
      )}
    </div>
  );
};
